import { Link } from '@tanstack/react-router'
import { ChevronRight, Inbox, Users } from 'lucide-react'
import { BalansBalk } from '@/components/ui/BalansBalk'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatusDot } from '@/components/ui/StatusDot'
import { Verdeelbalk } from '@/components/ui/Verdeelbalk'
import { formatPeriode, formatProcent, formatUren } from '@/lib/format'
import { bepaalStatus, SOORT_KLEUR, SOORT_LABEL, telSoorturenOp, verdeling } from '@/lib/inzet'
import { useTeam } from '@/lib/medewerker-api'
import type { Inzetregel, TeamOverzicht } from '@/lib/medewerker-types'
import { useSemesterKeuze } from '@/lib/semester-context'
import type { SemesterStatus } from '@/lib/semester-types'

const SEMESTERSTATUS: Record<SemesterStatus, string> = {
  concept: 'Concept',
  actief: 'Actief',
  afgerond: 'Afgerond',
}

export function TeamPage() {
  const { semester, semesterId, isLoading: semesterLaadt } = useSemesterKeuze()
  const team = useTeam(semesterId)

  const laadt = semesterLaadt || team.isLoading

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team"
        description={
          semester ? (
            <span>
              {semester.naam}
              <span className="text-ink-faint"> · </span>
              {formatPeriode(semester.startDatum, semester.eindDatum)}
            </span>
          ) : null
        }
        actions={
          semester ? (
            <span className="inline-flex items-center rounded-md bg-fill-1 px-2 py-1 text-xs font-medium text-ink-soft ring-1 ring-inset ring-hairline">
              {SEMESTERSTATUS[semester.status]}
            </span>
          ) : null
        }
      />

      {team.isError ? (
        <ErrorState error={team.error} onRetry={() => team.refetch()} />
      ) : laadt ? (
        <TeamSkelet />
      ) : !team.data || team.data.medewerkers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nog geen medewerkers in dit semester"
          description="Zodra de aanstellingen zijn ingelezen verschijnt hier de verdeling per medewerker."
        />
      ) : (
        <>
          <Kerncijfers overzicht={team.data} />
          <Medewerkerlijst overzicht={team.data} />
          <NogTeVerdelen regels={team.data.nietToegewezen} />
        </>
      )}
    </div>
  )
}

const BEZETTING_TEKST: Record<ReturnType<typeof bepaalStatus>, string> = {
  overbezet: 'text-status-overbezet',
  onderbezet: 'text-status-onderbezet',
  in_orde: 'text-status-in-orde',
  aanstelling_onbekend: 'text-ink',
}

function Kerncijfers({ overzicht }: { overzicht: TeamOverzicht }) {
  const bezetting =
    overzicht.totaalBeschikbaar > 0
      ? (overzicht.totaalToegewezen / overzicht.totaalBeschikbaar) * 100
      : null
  const status = bepaalStatus(overzicht.totaalToegewezen, overzicht.totaalBeschikbaar || null)

  const cellen = [
    { label: 'Toegewezen', waarde: `${formatUren(overzicht.totaalToegewezen)} u`, kleur: 'text-ink' },
    { label: 'Beschikbaar', waarde: `${formatUren(overzicht.totaalBeschikbaar)} u`, kleur: 'text-ink' },
    {
      label: 'Bezetting',
      waarde: bezetting === null ? '-' : formatProcent(bezetting),
      kleur: BEZETTING_TEKST[status],
    },
  ]

  return (
    <div className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="grid grid-cols-1 divide-y divide-hairline sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {cellen.map((cel) => (
          <div key={cel.label} className="px-5 py-4">
            <p className="label-caps">{cel.label}</p>
            <p
              className={`num mt-1 text-left text-[22px] font-semibold tracking-[-0.02em] ${cel.kleur}`}
            >
              {cel.waarde}
            </p>
          </div>
        ))}
      </div>

      <Verdeelbalk
        vakken={verdeling(telSoorturenOp(overzicht.medewerkers))}
        className="border-t border-hairline px-5 py-4"
      />
    </div>
  )
}

function Medewerkerlijst({ overzicht }: { overzicht: TeamOverzicht }) {
  return (
    <div className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="hidden grid-cols-[11rem_minmax(0,1fr)_1.25rem] gap-4 border-b border-hairline px-4 py-2.5 sm:grid">
        <span className="label-caps">Medewerker</span>
        <span className="label-caps">Toegewezen tegenover aanstelling</span>
        <span />
      </div>

      <div className="divide-y divide-hairline">
        {overzicht.medewerkers.map((medewerker) => (
          <Link
            key={medewerker.medewerkerId}
            to="/medewerker/$id"
            params={{ id: medewerker.medewerkerId }}
            className="flex flex-col gap-2.5 px-4 py-3.5 transition-colors duration-150 hover:bg-fill-1 sm:grid sm:grid-cols-[11rem_minmax(0,1fr)_1.25rem] sm:items-center sm:gap-4"
          >
            <span className="flex min-w-0 items-center gap-2">
              <StatusDot status={medewerker.status} />
              <span className="truncate text-sm font-medium text-ink">{medewerker.naam}</span>
            </span>
            <BalansBalk
              toegewezen={medewerker.totaal}
              beschikbaar={medewerker.beschikbaar}
              status={medewerker.status}
            />
            <ChevronRight className="hidden h-4 w-4 text-ink-faint sm:block" strokeWidth={1.85} />
          </Link>
        ))}
      </div>
    </div>
  )
}

function NogTeVerdelen({ regels }: { regels: Inzetregel[] }) {
  if (regels.length === 0) return null
  const totaal = regels.reduce((som, regel) => som + regel.uren, 0)

  return (
    <section className="rounded-card border border-hairline bg-fill-1 px-5 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Inbox className="h-4 w-4 text-ink-faint" strokeWidth={1.85} />
          Nog te verdelen
        </h2>
        <p className="num text-sm text-ink-soft">
          {formatUren(totaal)} u over {regels.length} {regels.length === 1 ? 'taak' : 'taken'}
        </p>
      </div>

      <ul className="mt-3 divide-y divide-hairline">
        {regels.map((regel, index) => (
          <li
            key={`${regel.omschrijving}-${index}`}
            className="grid grid-cols-[minmax(0,1fr)_4.5rem] items-baseline gap-3 py-2"
          >
            <span className="min-w-0">
              <span className="text-sm text-ink">{regel.omschrijving}</span>
              {regel.groepCode ? (
                <span className="ml-2 text-xs text-ink-faint">{regel.groepCode}</span>
              ) : null}
              <span className="ml-2 inline-flex items-center gap-1.5 text-xs text-ink-soft">
                <span
                  aria-hidden="true"
                  className={`h-2 w-2 rounded-[2px] ${SOORT_KLEUR[regel.soort].vlak}`}
                />
                {SOORT_LABEL[regel.soort]}
              </span>
            </span>
            <span className="num text-sm text-ink-soft">{formatUren(regel.uren)} u</span>
          </li>
        ))}
      </ul>

      <Link to="/taken" className="btn-secondary mt-4">
        Naar de taken
      </Link>
    </section>
  )
}

function TeamSkelet() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-[86px] w-full rounded-card" />
      <div className="overflow-hidden rounded-card bg-surface shadow-card">
        <div className="divide-y divide-hairline">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-center gap-4 px-4 py-4">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-2.5 flex-1" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
